// src/EditMaestranzaForm.js
import React, { useEffect, useState } from 'react';
import { Box, Button, TextField, Stack, Typography, Paper, List, ListItemButton } from '@mui/material';
import { fetchJson } from './api';
import RoleSelect from './components/RoleSelect';
import useDirtyGuard from './hooks/useDirtyGuard';

// YYYY-MM-DD -> YYYY-MM-DD (+N giorni), gestisce fine mese/anno
const addDaysYmd = (ymd, days) => {
  if (!ymd) return '';
  const s = String(ymd).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  const dt = new Date(Date.UTC(+m[1], +m[2]-1, +m[3]));
  dt.setUTCDate(dt.getUTCDate() + (days || 0));
  const y = dt.getUTCFullYear();
  const mo = String(dt.getUTCMonth()+1).padStart(2,'0');
  const d  = String(dt.getUTCDate()).padStart(2,'0');
  return `${y}-${mo}-${d}`;
};

// Se il locale è avanti a UTC (Roma), pre-compensa di +1 giorno
const bumpForLocalMidnightBug = (ymd) => {
  const tz = new Date().getTimezoneOffset(); // Roma: -60 o -120
  return (tz <= -60) ? addDaysYmd(ymd, 1) : ymd;
};

// "YYYY-MM-DD" -> "YYYY-MM-DDT12:00:00.000Z" (dopo bump)
const toUtcNoonIsoSafe = (ymd) => {
  const d = bumpForLocalMidnightBug(ymd);
  if (!d) return '';
  return `${d}T12:00:00.000Z`;
};


// Converte "YYYY-MM-DD" in "YYYY-MM-DDT12:00:00.000Z" (mezzogiorno UTC)
const toUtcNoonIso = (ymd) => {
  if (!ymd) return '';
  const d = String(ymd).slice(0, 10); // prendi solo la parte data
  const m = d.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  return `${m[1]}-${m[2]}-${m[3]}T12:00:00.000Z`;
};

// Normalizza date in YYYY-MM-DD (accetta già formati dd/mm/yyyy, ecc.)
const toIsoDate = (s) => {
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  const dt = new Date(s);
  return isNaN(dt) ? '' : dt.toISOString().slice(0, 10);
};

/**
 * Props:
 * - maestranza: { id, azienda, produzione, ruolo?, ruoli?, data_inizio_contratto?, data_fine_contratto? }
 * - onMaestranzaUpdated?, onSaved?, onClose?, onCancel?
 */


// Converte stringhe in "YYYY-MM-DD" senza slittamenti di fuso
const toDateOnly = (s) => {
  if (!s) return '';
  const str = String(s);
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`; // già formato puro
  const d = new Date(str);
  if (isNaN(d)) return '';
  const y = d.getFullYear();
  const m2 = String(d.getMonth() + 1).padStart(2, '0');
  const d2 = String(d.getDate()).padStart(2, '0');
  return `${y}-${m2}-${d2}`;
};

export default function EditMaestranzaForm({
  maestranza,
  onMaestranzaUpdated,
  onSaved,
  onClose,
  onCancel,
}) {
  // Stato form
  const [azienda, setAzienda] = useState(maestranza?.azienda || '');
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [produzione, setProduzione] = useState(maestranza?.produzione || '');

  const initialRuoli =
    (Array.isArray(maestranza?.ruoli) && maestranza.ruoli.length > 0)
      ? maestranza.ruoli
      : (maestranza?.ruolo ? [maestranza.ruolo] : []);
  const [ruoli, setRuoli] = useState(initialRuoli);

  const [dataInizio, setDataInizio] = useState(toDateOnly(maestranza?.data_inizio_contratto) || '');
  const [dataFine, setDataFine] = useState(toDateOnly(maestranza?.data_fine_contratto) || '');


  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [isTentative, setIsTentative] = useState(maestranza?.is_tentative || false);

  // Master ruoli per RoleSelect
  const [rolesMaster, setRolesMaster] = useState({});
  const [loadingRoles, setLoadingRoles] = useState(true);

  // Dirty guard (uscita senza salvataggio)
  const { markDirty, resetDirty, confirmIfDirty } = useDirtyGuard();

  const searchCompanies = React.useCallback(async (q) => {
    if (!q || q.length < 2) { setCompanySuggestions([]); return; }
    try {
      const data = await fetchJson(`/maestranze/companies/search?q=${encodeURIComponent(q)}`);
      setCompanySuggestions(Array.isArray(data) ? data : []);
      setShowSuggestions(true);
    } catch { setCompanySuggestions([]); }
  }, []);

  // Carica ruoli (usa fetchJson per avere auth corretta)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoadingRoles(true);
        const payload = await fetchJson('/ruoli');
        if (alive) setRolesMaster(payload || {});
      } catch (e) {
        console.error('Caricamento ruoli fallito:', e?.message || e);
        if (alive) setRolesMaster({});
      } finally {
        if (alive) setLoadingRoles(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validazioni minime
    if (!azienda || !produzione || !dataFine || !ruoli || ruoli.length === 0 || !ruoli[0]) {
      setError('Compila tutti i campi obbligatori.');
      return;
    }

    setSaving(true);
    try {
      const payloadResp = await fetchJson(`/maestranze/${maestranza.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          azienda,
          produzione,
          ruolo: ruoli[0],      // back-compat
          ruoli,                // lista completa
          data_inizio_contratto: toUtcNoonIso(dataInizio || dataFine),
          data_fine_contratto: toUtcNoonIso(dataFine),
          is_tentative: isTentative,
        }),
      });

      // Successo
      resetDirty();
      onMaestranzaUpdated?.(payloadResp);
      onSaved?.(payloadResp);
      onClose?.();
      onCancel?.();
    } catch (err) {
      setError(err.message || 'Errore durante l’aggiornamento.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      onChangeCapture={markDirty}
      sx={{ width: '100%', maxWidth: 420, display: 'grid', gap: 2, boxSizing: 'border-box' }}
    >
      <Typography variant="h6" sx={{ mb: 1 }}>Modifica Produzione</Typography>

      <Box sx={{ position: 'relative' }}>
        <TextField
          label="Società"
          value={azienda}
          onChange={(e) => { setAzienda(e.target.value); searchCompanies(e.target.value); }}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          onFocus={() => { if (companySuggestions.length > 0) setShowSuggestions(true); }}
          required
          fullWidth
          autoComplete="off"
        />
        {showSuggestions && companySuggestions.length > 0 && (
          <Paper elevation={8} sx={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
            bgcolor: '#1a1d23', border: '1px solid rgba(212,165,80,0.3)', borderRadius: 1, mt: 0.5
          }}>
            <List dense disablePadding>
              {companySuggestions.map(c => (
                <ListItemButton key={c.id}
                  onMouseDown={() => { setAzienda(c.name); setShowSuggestions(false); setCompanySuggestions([]); }}
                  sx={{ py: 0.8, '&:hover': { bgcolor: 'rgba(212,165,80,0.08)' } }}
                >
                  <Typography sx={{ fontSize: 13, color: '#f0ece4' }}>{c.name}</Typography>
                </ListItemButton>
              ))}
            </List>
          </Paper>
        )}
      </Box>

      <TextField
        label="Titolo programma"
        value={produzione}
        onChange={(e) => setProduzione(e.target.value)}
        required
        fullWidth
      />

      <RoleSelect
        rolesMaster={rolesMaster}
        label={loadingRoles ? 'Carico ruoli…' : 'Ruolo'}
        value={ruoli}
        onChange={(vals) => setRuoli(Array.isArray(vals) ? vals : (vals ? [vals] : []))}
        required
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField
          label="Data inizio"
          type="date"
          value={dataInizio}
          onChange={(e) => setDataInizio(e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <TextField
          label="Data fine"
          type="date"
          value={dataFine}
          onChange={(e) => setDataFine(e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
          required
        />
      </Stack>

      {error && <Typography color="error">{error}</Typography>}

      <Box sx={{ display:'flex', alignItems:'center', gap:1, mt:1, mb:1 }}>
        <input type="checkbox" id="isTentativeEdit" checked={isTentative} onChange={e => setIsTentative(e.target.checked)}
          style={{ width:16, height:16, accentColor:'#a78bfa', cursor:'pointer' }} />
        <label htmlFor="isTentativeEdit" style={{ fontSize:13, color: isTentative ? '#a78bfa' : '#8a8a9a', cursor:'pointer', userSelect:'none' }}>
          Non confermata
        </label>
      </Box>
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button
          type="button"
          variant="outlined"
          onClick={async () => {
            const ok = await confirmIfDirty();
            if (!ok) return;
            onClose?.();
            onCancel?.();
          }}
        >
          Annulla
        </Button>
        <Button type="submit" variant="contained" disabled={saving}>
          {saving ? 'Salvo…' : 'Salva'}
        </Button>
      </Stack>
    </Box>
  );
}
